import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  makeStyles,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
  Chip,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Typography,
} from '@material-ui/core';
import {
  DragIndicator as DragIcon,
  ConfirmationNumber as TicketIcon,
  Chat as ChatIcon,
  Visibility as PreviewIcon,
  Warning as WarningIcon,
} from '@material-ui/icons';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { LayoutProps } from '../../types';
import { useJiraTickets, usePlanPreview } from '../../hooks';
import { AgentChatComponent } from '../AgentChatComponent';
import { STSTicketPanel } from './STSTicketPanel';
import { STSPlanPreview } from './STSPlanPreview';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    height: '100%',
    overflow: 'hidden',
    backgroundColor: theme.palette.background.default,
    flexDirection: 'column',
  },
  // ── Desktop: horizontal 3-panel ──────────────────────────────────────────
  desktopRow: {
    display: 'flex',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  panel: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  ticketPanel: {
    width: 200,
    flexShrink: 0,
    borderRight: `1px solid ${theme.palette.divider}`,
  },
  chatPanel: {
    flex: 1,
    minWidth: 0,
    padding: theme.spacing(1.5),
  },
  planPanel: {
    minWidth: 280,
    maxWidth: '50%',
    borderLeft: `1px solid ${theme.palette.divider}`,
  },
  resizer: {
    width: 11,
    flexShrink: 0,
    cursor: 'col-resize',
    backgroundColor: 'transparent',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 10,
    transition: 'background-color 0.2s ease',
    '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)' },
    '&:active': { backgroundColor: 'rgba(0,0,0,0.1)' },
  },
  dragIcon: { fontSize: 14, color: '#94a3b8' },
  // ── Mobile/Tablet: tab-based ──────────────────────────────────────────────
  tabBar: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    flexShrink: 0,
  },
  tab: {
    minWidth: 0,
    flex: 1,
    padding: theme.spacing(1, 2),
    fontSize: '0.875rem',
    fontWeight: 500,
    textTransform: 'none',
  },
  tabContent: { display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) },
  tabPanel: {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
}));

export const DesktopLayout: React.FC<LayoutProps> = ({
  namespace,
  backPath,
  basePath,
  showAgentsSidebar,
  stsMode,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

  const identityApi = useApi(identityApiRef);
  const [sessionId, setSessionId] = useState<string>('');
  const [userEmail, setUserEmail] = useState<string>('');

  useEffect(() => {
    identityApi.getProfileInfo().then(profile => {
      setUserEmail(profile.email ?? '');
    });
  }, []);

  const [rightWidth, setRightWidth] = useState(33);
  const [isResizing, setIsResizing] = useState(false);
  const sendMessageRef = useRef<((message: string, overrideSessionId?: string) => void) | null>(null);

  const [isDirty, setIsDirty] = useState(false);
  const [pendingTicket, setPendingTicket] = useState<any>(null);
  const [dirtyDialog, setDirtyDialog] = useState(false);

  // Mobile tab state
  const [activeTab, setActiveTab] = useState(1);

  const { tickets, loading, selectedTicket, selectTicket, refreshTickets } = useJiraTickets();
  const { planMarkdown, setPlanMarkdown, fetchPlan, clearPlan } = usePlanPreview();

  useEffect(() => {
    if (isDirty) {
      const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
      window.addEventListener('beforeunload', handler);
      return () => window.removeEventListener('beforeunload', handler);
    }
    return undefined;
  }, [isDirty]);

  // ── Resizer (desktop only) ────────────────────────────────────────────────
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizing) return;
    const pct = ((window.innerWidth - e.clientX) / window.innerWidth) * 100;
    setRightWidth(Math.max(20, Math.min(50, pct)));
  }, [isResizing]);

  const handleMouseUp = useCallback(() => setIsResizing(false), []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
    }
    return undefined;
  }, [isResizing, handleMouseMove, handleMouseUp]);

  // ── Ticket selection ──────────────────────────────────────────────────────
  const doSelectTicket = (ticket: any) => {
    selectTicket(ticket);
    if (ticket) {
      const newSessionId = `${ticket.ticketNumber}-${userEmail}-${Date.now()}`;
      setSessionId(newSessionId);
      fetchPlan(ticket.ticketNumber);
      sendMessageRef.current?.(`I have selected ticket ${ticket.ticketNumber}`, newSessionId);
      if (isMobile) setActiveTab(1);
    } else {
      clearPlan();
    }
  };

  const handleTicketSelect = (ticket: any) => {
    if (isDirty) {
      setPendingTicket(ticket);
      setDirtyDialog(true);
    } else {
      doSelectTicket(ticket);
    }
  };

  const handleDirtyConfirm = () => {
    setDirtyDialog(false);
    setIsDirty(false);
    doSelectTicket(pendingTicket);
    setPendingTicket(null);
  };

  const handleDirtyCancel = () => {
    setDirtyDialog(false);
    setPendingTicket(null);
  };

  const handleTabChange = (_: React.ChangeEvent<{}>, val: number) => {
    if (isDirty && activeTab === 2 && val !== 2) {
      setPendingTicket(null);
      setDirtyDialog(true);
    } else {
      setActiveTab(val);
    }
  };

  const agentChat = (
    <AgentChatComponent
      namespace={namespace}
      backPath={backPath}
      basePath={basePath}
      showAgentsSidebar={showAgentsSidebar}
      stsMode={stsMode}
      onSendMessage={fn => { sendMessageRef.current = fn; }}
      ticketSelected={!!selectedTicket}
      sessionId={sessionId}
    />
  );

  const ticketPanel = (
    <STSTicketPanel
      tickets={tickets}
      loading={loading}
      selectedTicket={selectedTicket}
      onTicketSelect={handleTicketSelect}
      onRefresh={refreshTickets}
      viewMode="table"
    />
  );

  const planPanel = (
    <STSPlanPreview
      planMarkdown={planMarkdown}
      selectedTicket={selectedTicket}
      onDirtyChange={setIsDirty}
      onPlanUpdated={newPlan => setPlanMarkdown(newPlan)}
      sendMessage={msg => sendMessageRef.current?.(msg)}
    />
  );

  const dirtyDialogEl = (
    <Dialog open={dirtyDialog} onClose={handleDirtyCancel} maxWidth="xs" fullWidth PaperProps={{ style: { borderRadius: 16, overflow: 'hidden' } }}>
      <Box style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '20px 24px 16px', borderBottom: '1px solid #e0e0e0', backgroundColor: '#fff8e1' }}>
        <Box style={{ width: 36, height: 36, borderRadius: 10, backgroundColor: '#fff3cd', border: '1px solid #ffe082', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <WarningIcon style={{ fontSize: 20, color: '#f59e0b' }} />
        </Box>
        <Typography style={{ fontWeight: 700, fontSize: '1rem', color: '#111827' }}>Unsaved Changes</Typography>
      </Box>
      <DialogContent style={{ padding: 24 }}>
        <DialogContentText style={{ fontSize: '0.875rem', lineHeight: 1.6, color: '#6b7280' }}>
          You have unsaved edits to the current plan. Switching will discard them.
        </DialogContentText>
      </DialogContent>
      <DialogActions style={{ padding: '16px 24px', borderTop: '1px solid #e0e0e0', backgroundColor: '#fafafa', display: 'flex', gap: 8 }} disableSpacing>
        <Button onClick={handleDirtyCancel} style={{ textTransform: 'none', fontSize: '0.82rem', borderRadius: 10, color: '#1976d2', border: '1px solid #90caf9', backgroundColor: '#e3f2fd' }}>
          Stay here
        </Button>
        <Box flex={1} />
        <Button onClick={handleDirtyConfirm} style={{ textTransform: 'none', fontSize: '0.82rem', borderRadius: 10, color: '#6b7280', border: '1px solid #e0e0e0' }}>
          Discard & continue
        </Button>
      </DialogActions>
    </Dialog>
  );

  // ── Mobile/Tablet: tab layout ─────────────────────────────────────────────
  if (isMobile) {
    return (
      <Box className={classes.root}>
        <Box className={classes.tabBar}>
          <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth" indicatorColor="primary" textColor="primary">
            <Tab className={classes.tab} label={
              <Box className={classes.tabContent}>
                <TicketIcon fontSize="small" />
                <span>Tickets</span>
                {selectedTicket && <Chip size="small" label="1" color="primary" />}
              </Box>
            } />
            <Tab className={classes.tab} label={
              <Box className={classes.tabContent}>
                <ChatIcon fontSize="small" />
                <span>Chat</span>
              </Box>
            } />
            <Tab className={classes.tab} label={
              <Box className={classes.tabContent}>
                <PreviewIcon fontSize="small" />
                <span>Plan</span>
                {isDirty && <Chip size="small" label="•" style={{ backgroundColor: '#f59e0b', color: '#fff', minWidth: 20 }} />}
              </Box>
            } />
          </Tabs>
        </Box>
        <Box className={classes.tabPanel} style={{ display: activeTab === 0 ? 'flex' : 'none' }}>{ticketPanel}</Box>
        <Box className={classes.tabPanel} style={{ display: activeTab === 1 ? 'flex' : 'none' }}>{agentChat}</Box>
        <Box className={classes.tabPanel} style={{ display: activeTab === 2 ? 'flex' : 'none' }}>{planPanel}</Box>
        {dirtyDialogEl}
      </Box>
    );
  }

  // ── Desktop: 3-panel layout ───────────────────────────────────────────────
  return (
    <Box className={classes.root}>
      <Box className={classes.desktopRow}>
        <Box className={`${classes.panel} ${classes.ticketPanel}`}>
          {ticketPanel}
        </Box>
        <Box className={`${classes.panel} ${classes.chatPanel}`} style={{ width: `calc(100% - 200px - ${rightWidth}%)` }}>
          {agentChat}
        </Box>
        <Box className={classes.resizer} onMouseDown={handleMouseDown}>
          <DragIcon className={classes.dragIcon} />
        </Box>
        <Box className={`${classes.panel} ${classes.planPanel}`} style={{ width: `${rightWidth}%` }}>
          {planPanel}
        </Box>
      </Box>
      {dirtyDialogEl}
    </Box>
  );
};
